
import { z } from 'zod';
import type { RefinementCtx } from 'zod';
import { convertToTitleCase } from '../util.validation.z';

/**
 * Base Zod schema representing a Mongoose document.
 * 
 * This schema validates `_id` and `__v` fields, which are common to all Mongoose documents.
 * 
 * @example
 * documentSchema.parse(yourDocument);
 */
export const documentSchema = z.object({
    _id: z.string(),
    // __v: z.string(), // TODO: is schema versioning nessassary ? 
});

/**
 * Zod schema representing a timestamped Mongoose document.
 * 
 * This schema builds upon {@link documentSchema} by adding validations for `createdAt` and `updatedAt` fields.
 * 
 * @example
 * timestampedDocumentSchema.parse(yourDocument);
 */
export const timestampedDocSchema = documentSchema.merge(z.object({
    createdAt: z.date({
        required_error: "Object has timestamps set to true, but lacks a 'createdAt' timestamp."
    }),
    updatedAt: z.date({
        required_error: "Object has timestamps set to true, but lacks an 'updatedAt' timestamp."
    }),
}));

/**
 * Generates a Zod schema for validating required strings with custom error messages.
 * 
 * @param fieldName - Name of the field being validated.
 * @returns A Zod schema instance for required string validation with custom error messages.
 */
export const requiredString = (fieldName: string, min: number = 1, max: number = 100000) => {
    let prettyFieldName = convertToTitleCase(fieldName);
    return z.string({
        required_error: `${fieldName} | ${prettyFieldName} is required`,
        invalid_type_error: `${fieldName} | ${prettyFieldName} must be a string`
    })
        .min(min,
            `${fieldName} | ${prettyFieldName} must be at least ${min} characters long`)
        .max(max,
            `${fieldName} | ${prettyFieldName} must be at most ${max} characters long`)
        .superRefine((val: string, ctx: RefinementCtx) => {
            if (val.trim().length != 0) {
                return true;
            }
            ctx.addIssue({
                code: "custom",
                message: `${ctx.path.join(".")} | ${prettyFieldName} must not be composed entirely of whitespaces.`,
            });

        });
};


/** 
 * allows undefined , the rest triggers validation 
 */
export const optionalString = (fieldName: string) => {
    return requiredString(fieldName).optional();
}

